<template>
    <Head :title="`Edit Template: ${template.title}`" />

    <div class="h-screen flex flex-col bg-gray-100 dark:bg-gray-900">
        <!-- Header -->
        <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div class="flex justify-between items-center">
                <div class="flex items-center">
                    <Link
                        :href="route('checklists.templates.show', template.id)"
                        class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mr-4"
                    >
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                    </Link>
                    <div>
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Template Builder</h1>
                        <p class="text-sm text-gray-600 dark:text-gray-400">{{ template.title }}</p>
                    </div>
                </div>
                <div class="flex items-center space-x-3">
                    <!-- Preview Toggle -->
                    <div class="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                        <button
                            @click="previewMode = false"
                            type="button"
                            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
                            :class="!previewMode ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'"
                        >
                            <svg class="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                        </button>
                        <button
                            @click="previewMode = true"
                            type="button"
                            class="px-3 py-1.5 text-sm font-medium rounded-md transition-colors"
                            :class="previewMode ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400'"
                        >
                            <svg class="w-4 h-4 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Preview
                        </button>
                    </div>

                    <button
                        @click="saveTemplate"
                        :disabled="form.processing"
                        type="button"
                        class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                    >
                        <svg v-if="form.processing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {{ form.processing ? 'Saving...' : 'Save Template' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- 3-Column Builder Layout -->
        <div class="flex-1 overflow-hidden flex">
            <!-- Left: Field Palette (hidden in preview mode) -->
            <div v-if="!previewMode" class="w-64 flex-shrink-0">
                <FieldPalette :template-type="template.type" />
            </div>

            <!-- Center: Canvas -->
            <div class="flex-1">
                <BuilderCanvas
                    v-model:sections="sections"
                    :selected-field-id="selectedField?.id"
                    :preview-mode="previewMode"
                    @field-selected="onFieldSelected"
                    @field-added="onFieldAdded"
                />
            </div>

            <!-- Right: Properties Panel (hidden in preview mode) -->
            <div v-if="!previewMode" class="w-96 flex-shrink-0">
                <FieldPropertiesPanel
                    :field="selectedField"
                    :sections="sections"
                    @save="onFieldSaved"
                    @close="selectedField = null"
                />
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link } from '@inertiajs/vue3';
import route from '@/Utils/route';
import { useSecureForm } from '@/Composables/useSecureForm';
import FieldPalette from '@/Components/Checklists/Builder/FieldPalette.vue';
import BuilderCanvas from '@/Components/Checklists/Builder/BuilderCanvas.vue';
import FieldPropertiesPanel from '@/Components/Checklists/Builder/FieldPropertiesPanel.vue';

const props = defineProps({
    template: Object,
    types: Array,
    errors: Object,
    companyOwner: {
        type: Object,
        default: null,
    },
});

// Form data
const form = useSecureForm({
    title: props.template.title || '',
    description: props.template.description || '',
    type_id: props.template.type_id || '',
    scoring_enabled: props.template.scoring_enabled || false,
    passing_score: props.template.passing_score || 80,
    scoring_method: props.template.scoring_method || 'percentage',
    sections: [], // Initialize sections in form
});

// Sections and fields
const sections = ref(props.template.sections || []);
const selectedField = ref(null);
const previewMode = ref(false);

const onFieldSelected = (field) => {
    selectedField.value = field;
};

const onFieldAdded = (field) => {
    // Auto-select newly added field
    selectedField.value = field;
};

const onFieldSaved = (updatedField) => {
    // Find and update the field in sections
    for (const section of sections.value) {
        const fieldIndex = section.fields.findIndex(f => f.id === updatedField.id);
        if (fieldIndex !== -1) {
            section.fields[fieldIndex] = updatedField;
            break;
        }
    }
    selectedField.value = null;
};

const saveTemplate = () => {
    // Prepare data for submission
    form.sections = sections.value.map(section => ({
        id: section.id, // Always send the ID (temporary or real)
        title: section.title,
        description: section.description,
        display_order: section.display_order ?? section.sort_order ?? 0,
        fields: (section.fields || []).map(field => ({
            id: field.id, // Always send the ID (temporary or real) - backend will distinguish
            label: field.label,
            help_text: field.help_text,
            field_type: field.field_type,
            field_config: field.field_config,
            is_required: field.is_required,
            allow_notes: field.allow_notes,
            require_notes: field.require_notes,
            allow_photos: field.allow_photos,
            require_photos: field.require_photos,
            allow_attachments: field.allow_attachments,
            require_attachments: field.require_attachments,
            is_primary: field.is_primary,
            parent_field_id: field.parent_field_id,
            dependency_rule: field.dependency_rule,
            sort_order: field.sort_order,
            points: field.points,
            desired_answer: field.desired_answer,
        })),
    }));

    const url = route('checklists.templates.update', props.template.id);
    form.put(url);
};
</script>
